import type { MenuItem, MenuSection } from '@/domains/admin-panel/store/use-menu-store/useMenuStore'
import type { MenuItemGroup, SupportDetailsInput } from '@/domains/admin-panel/utils/focus-sidebar/focusSidebar.types'

import { groupConsecutive } from '@/utils/collection-util/collectionUtil'

/**
 * Groups a space's pages under their group head, keeping the order the backend sent. Only
 * consecutive items share a group, so a group name that repeats later starts a new head.
 */
export const groupMenuItems = (items: MenuItem[]): MenuItemGroup[] =>
  groupConsecutive(items, item => item.group ?? null).map(({ key, items: grouped }) => ({ name: key, items: grouped }))

/** Where a click on a space row goes: the space's own page, else its first page. */
export const firstPathOf = (section: MenuSection): string | undefined =>
  section.path || section.items.find(item => item.path)?.path || undefined

export const isExternalPath = (path: string | undefined): boolean =>
  !!path && (path.startsWith('http') || path.startsWith('//'))

/** The backend's `active` flag wins; otherwise the page is current when its path matches. */
export const isMenuItemActive = (item: MenuItem, currentPath: string): boolean => {
  if (item.active) return true
  if (!item.path || isExternalPath(item.path)) return false

  // A report's link carries its default filters as a query string; the page is current whatever its filters.
  return new URL(item.path, window.location.origin).pathname === currentPath
}

export const isSectionCurrent = (section: MenuSection, currentPath: string): boolean =>
  section.items.some(item => isMenuItemActive(item, currentPath)) || section.path === currentPath

/** A space with one page and no page of its own is a plain link, with no list under it. */
export const isSinglePageSection = (section: MenuSection): boolean => section.items.length <= 1

/** The line the About row copies, so a support ticket carries the exact build. */
export const buildSupportDetails = ({ version, host, userAgent, now }: SupportDetailsInput): string =>
  ['Twine', version ? `v${version}` : 'version unknown', '·', host, '·', userAgent, '·', now.toISOString()].join(' ')
